import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Users2, Clock, PhoneCall, Send, Calendar, Award, ExternalLink, Loader2, RefreshCw, GitCompareArrows } from "lucide-react";
import api from "@/lib/api";
import { ScoreDial, ScoreBreakdown } from "@/components/performance/ScoreComponents";

const PERIODS = [
  { k: "today", t: "Hoje" }, { k: "week", t: "Semana" }, { k: "month", t: "Mês" },
  { k: "last_30d", t: "30 dias" }, { k: "last_90d", t: "90 dias" }, { k: "all", t: "Total" },
];

const CLASS_LABEL = { excellent: "Excelente", very_good: "Muito Bom", improving: "A melhorar", insufficient: "Insuficiente", critical: "Crítico" };
const CLASS_TEXT = { excellent: "text-[hsl(var(--success))]", very_good: "text-[hsl(var(--success))]", improving: "text-[hsl(var(--warning))]", insufficient: "text-[hsl(var(--warning))]", critical: "text-[hsl(var(--danger))]" };

function fmtDT(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString("pt-PT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

function fmtN(v) { return new Intl.NumberFormat("pt-PT").format(Number(v || 0)); }

function KpiTile({ label, value, sub, icon: Icon, testid }) {
  return (
    <div className="bg-card border border-border rounded-xl p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-3.5 h-3.5 text-foreground" />
        <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">{label}</span>
      </div>
      <div className="font-display text-2xl text-foreground">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

export default function OperatorDetail() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [period, setPeriod] = useState(searchParams.get("period") || "last_30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const reqKeyRef = useRef("");

  const load = async () => {
    setLoading(true);
    const key = `${id}|${period}`;
    reqKeyRef.current = key;
    try {
      const r = await api.get(`/performance/operators/${id}?period=${period}`);
      if (reqKeyRef.current === key) setData(r.data);
    } catch (e) {
      if (reqKeyRef.current === key) {
        toast.error(e.response?.data?.detail || "Erro a carregar operador");
      }
    } finally {
      if (reqKeyRef.current === key) setLoading(false);
    }
  };

  useEffect(() => { load(); setSearchParams({ period }); }, [id, period]);  // eslint-disable-line

  if (loading) {
    return (
      <div className="p-8 lg:p-12 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> A carregar operador…
      </div>
    );
  }

  if (!data) return null;
  const s = data.score || {};
  const c = data.counts || {};
  const conv = data.conversions || {};
  const op = data.operational || {};

  return (
    <div className="p-8 lg:p-12 max-w-[1400px] mx-auto space-y-6" data-testid="operator-detail-root">
      <div>
        <Link to="/performance" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-2" data-testid="back-performance">
          <ArrowLeft className="w-3 h-3" /> Performance do Atendimento
        </Link>
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold">Operador de atendimento</div>
            <h1 className="font-display text-4xl lg:text-5xl tracking-tight" data-testid="operator-name">{data.operator?.name}</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {PERIODS.map(p => (
              <button key={p.k} onClick={() => setPeriod(p.k)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${period === p.k ? "bg-foreground text-background" : "bg-card border border-border text-foreground hover:bg-muted/40"}`}
                      data-testid={`op-period-${p.k}`}>{p.t}</button>
            ))}
            <button onClick={load} className="p-2 rounded-lg border border-border hover:bg-muted/40" data-testid="op-refresh">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Score hero */}
      <div className="bg-gradient-to-br from-[#0F172A] to-[#132E52] rounded-2xl p-6 lg:p-8 text-primary-foreground relative overflow-hidden" data-testid="score-hero">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(#fff 1px, transparent 1px)", backgroundSize: "16px 16px" }} />
        <div className="relative flex items-start justify-between gap-6 flex-wrap">
          <div className="flex items-center gap-6">
            <ScoreDial total={s.total || 0} emoji={s.emoji || "🔴"} size={140} strokeWidth={12} />
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-primary/80 font-semibold">Score de performance</div>
              <div className={`font-display text-3xl mt-1 ${CLASS_TEXT[s.classification] || "text-primary-foreground"}`}>{CLASS_LABEL[s.classification] || "—"}</div>
              <div className="text-xs text-primary-foreground/70 mt-1">
                {s.blockers?.length ? `⚠ ${s.blockers.length} regra(s) de bloqueio` : "Sem bloqueios activos"}
                {s.raw_total !== s.total && (
                  <> · <span className="line-through">{s.raw_total?.toFixed(1)}</span> antes de bloqueios</>
                )}
              </div>
            </div>
          </div>
          <div className="text-right text-sm">
            <div className="text-[10px] uppercase tracking-[0.15em] text-primary-foreground/50">Período</div>
            <div className="font-semibold">{PERIODS.find(p => p.k === period)?.t}</div>
            <div className="text-primary-foreground/60 text-xs mt-1">{fmtN(c.leads_received)} leads atribuídos</div>
          </div>
        </div>
      </div>

      {/* Operational KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <KpiTile label="Leads" value={fmtN(c.leads_received)} icon={Users2} testid="kpi-leads" />
        <KpiTile label="Contactados" value={fmtN(c.leads_contacted)} icon={PhoneCall} sub={`SLA ${data.sla?.compliance_pct?.toFixed(0) || 0}%`} testid="kpi-contacted" />
        <KpiTile label="Estimativas" value={fmtN(c.estimates_sent)} icon={Send} sub={op.avg_estimate_time_minutes !== null && op.avg_estimate_time_minutes !== undefined ? `${op.avg_estimate_time_minutes} min médio` : ""} testid="kpi-estimates" />
        <KpiTile label="RT agendadas" value={fmtN(c.rt_scheduled)} icon={Calendar} sub={`${conv.estimate_to_rt?.toFixed(0) || 0}% Est→RT`} testid="kpi-rt" />
        <KpiTile label="Negócios" value={fmtN(c.deals_closed)} icon={Award} sub={`${conv.lead_to_deal?.toFixed(1) || 0}% Lead→Neg`} testid="kpi-deals" />
        <KpiTile label="Sem tratamento" value={fmtN(op.leads_untouched)} icon={Users2} testid="kpi-untouched" />
        <KpiTile label="Tempo médio resposta" value={op.avg_response_minutes != null ? `${op.avg_response_minutes} min` : "—"} icon={Clock} sub={op.median_response_minutes != null ? `Mediana ${op.median_response_minutes}` : ""} testid="kpi-response" />
      </div>

      {/* Score breakdown + Leads */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-card border border-border rounded-2xl p-6 lg:p-8" data-testid="score-breakdown-panel">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Score breakdown</div>
              <h3 className="font-display text-2xl">9 factores da spec §16</h3>
            </div>
            <Link to={`/performance/compare?ids=${id}`} className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-lg bg-muted/40 border border-border hover:bg-muted font-semibold" data-testid="compare-btn">
              <GitCompareArrows className="w-3.5 h-3.5" /> Comparar
            </Link>
          </div>
          <ScoreBreakdown breakdown={s.breakdown || []} blockers={s.blockers || []} />
        </div>

        <div className="bg-card border border-border rounded-2xl p-6" data-testid="operator-leads-panel">
          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Leads deste operador</div>
            <h3 className="font-display text-lg">{data.leads?.length || 0} recentes</h3>
          </div>
          <div className="space-y-1 max-h-[600px] overflow-y-auto pr-1">
            {(data.leads || []).slice(0, 30).map(l => (
              <Link key={l.id} to={`/leads/${l.id}`}
                    className="block hover:bg-muted/40 rounded-md p-2 -mx-2 group"
                    data-testid={`op-lead-${l.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{l.name || "—"}</div>
                    <div className="text-[10px] text-muted-foreground">{fmtDT(l.created_at)} · {l.status}</div>
                  </div>
                  <ExternalLink className="w-3 h-3 text-muted-foreground/70 group-hover:text-foreground flex-shrink-0" />
                </div>
              </Link>
            ))}
            {(data.leads || []).length === 0 && (
              <div className="text-xs text-muted-foreground py-6 text-center">Sem leads no período.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
